import { Injectable, inject, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import type { Observable } from 'rxjs';
import { BehaviorSubject, fromEvent, merge, Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { SessionTimeoutDialogComponent } from '../../shared/components/session-timeout-dialog/session-timeout-dialog.component';

/**
 * Session Timeout Service
 *
 * Manages session timeout with the following behavior:
 * - After 15 minutes of inactivity, shows "Session Expired" dialog
 * - User must click Login button to refresh entire session
 * - No countdown timer - direct expiration after 15 minutes
 *
 * Flow:
 * User inactive for 15 minutes → Session expired popup appears
 * User clicks "Login" → Logout and redirect to login page for fresh authentication
 */
@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private readonly INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

  private dialog = inject(MatDialog);
  private authService = inject(AuthService);
  private ngZone = inject(NgZone);

  private destroy$ = new Subject<void>();
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private isDialogOpen = false;

  private sessionActive$ = new BehaviorSubject<boolean>(false);

  /**
   * Initialize session timeout monitoring
   * Call this when user successfully authenticates
   */
  startMonitoring(): void {
    this.cleanup();

    this.sessionActive$.next(true);

    // Start monitoring user activity
    this.setupActivityListeners();

    // Start inactivity timer
    this.resetInactivityTimer();
  }

  /**
   * Stop monitoring (on logout or session end)
   */
  stopMonitoring(): void {
    this.sessionActive$.next(false);
    this.cleanup();
  }

  /**
   * Reset the inactivity timer (called on user activity)
   */
  resetInactivityTimer(): void {
    // Clear existing timer
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    // Don't start new timer if dialog is already open or session not active
    if (this.isDialogOpen || !this.sessionActive$.value) {
      return;
    }

    // Start new inactivity timer
    this.inactivityTimer = setTimeout(() => {
      this.onInactivityTimeout();
    }, this.INACTIVITY_TIMEOUT);
  }

  /**
   * Called when inactivity timeout is reached (15 minutes)
   */
  private onInactivityTimeout(): void {
    // Prevent multiple dialogs from opening simultaneously
    if (this.isDialogOpen) {
      return;
    }

    // Show session expired dialog (no countdown)
    this.showTimeoutDialog();
  }

  /**
   * Show the session timeout dialog (expired after 15 minutes)
   */
  private showTimeoutDialog(): void {
    this.isDialogOpen = true;

    const dialogRef = this.dialog.open(SessionTimeoutDialogComponent, {
      width: '500px',
      disableClose: true,
      data: {
        isExpired: true // No countdown, direct expiration
      }
    });

    dialogRef.afterClosed().subscribe((action: 'login' | null) => {
      this.isDialogOpen = false;

      if (action === 'login') {
        this.handleLogin();
      }
    });
  }

  /**
   * Handle login action (refresh entire session)
   * Force logout and redirect to login page to get new token
   */
  private handleLogin(): void {
    this.stopMonitoring();

    // Force logout to clear existing token and redirect to login
    this.authService.logout();
  }

  /**
   * Setup listeners for user activity
   */
  private setupActivityListeners(): void {
    // Run outside Angular zone for better performance
    this.ngZone.runOutsideAngular(() => {
      const events$ = merge(
        fromEvent(document, 'mousedown'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'scroll', { capture: true }),
        fromEvent(document, 'touchstart')
      );

      events$
        .pipe(
          debounceTime(1000), // Debounce to avoid excessive timer resets
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          // Run timer reset inside Angular zone
          this.ngZone.run(() => {
            this.resetInactivityTimer();
          });
        });
    });
  }

  /**
   * Cleanup timers and subscriptions
   */
  private cleanup(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }

    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();

    this.isDialogOpen = false;
  }

  /**
   * Observable to check if session is active
   */
  get isSessionActive$(): Observable<boolean> {
    return this.sessionActive$.asObservable();
  }
}
